import {
    ADD_QUESTIONNAIRE,
    ADD_SECTIONS,
    RESET,
    SET_CURRENT_POSITION,
    SET_MESSAGE,
    SUBMIT_QUESTIONNAIRE_SUCCESS,
    SUBMIT_QUESTIONNAIRE_ERROR
} from "../actions/filled-questionnaire-action";

interface FilledAnswer {
    answersId: string;
    score?: number;
}

interface FilledQuestion {
    questionsId: string;
    filledAnswers: FilledAnswer[];
}

interface FilledPhase {
    phasesId: string;
    totalScore?: number;
    scorePercentage?: number;
    isCompleted?: boolean;
    filledQuestions: FilledQuestion[];
}

interface FilledSection {
    sectionsId: string;
    filledPhases: FilledPhase[];
}

const initialState = {
    questionnairesId: '',
    fullName: '',
    companyName: '',
    email: '',
    role: '',
    industry: '',
    country: '',
    companySize: '',
    filledSections: [] as FilledSection[],
    lastCompletedPhasesIds: [] as string[],
    currentSectionIndex: 0,
    currentPhaseIndex: 0,
    message: '',
    filledQuestionnaire: null as any,
    submissionError: null as any
};

const FilledQuestionnaireReducer = (state= initialState, action: any) => {
    switch(action.type) {
        case ADD_QUESTIONNAIRE:
            return {
                ...state,
                ...action.payload,
                filledQuestionnaire: action.payload
            }
        case ADD_SECTIONS:
            const { formData, lastCompletedPhasesId, phaseScore } = action.payload;
            const existingSectionIndex = state.filledSections.findIndex(
                (section: FilledSection) => section.sectionsId === formData.sectionsId
            );

            let updatedFilledSections: FilledSection[];

            if (existingSectionIndex >= 0) {
                // Section exists, update or add phase
                const existingSection = state.filledSections[existingSectionIndex];
                const existingPhaseIndex = existingSection.filledPhases.findIndex(
                    (phase: FilledPhase) => phase.phasesId === formData.phasesId
                );

                let updatedFilledPhases: FilledPhase[];

                if (existingPhaseIndex >= 0) {
                    // Phase exists, update it
                    const maxScore = formData.maxScore || 100; // Default to 100 if not provided
                    const scorePercentage = maxScore > 0 ? ((phaseScore || 0) / maxScore) * 100 : 0;
                    
                    updatedFilledPhases = existingSection.filledPhases.map((phase: FilledPhase, idx: number) =>
                        idx === existingPhaseIndex
                            ? {
                                  phasesId: formData.phasesId,
                                  totalScore: phaseScore || 0,
                                  scorePercentage: parseFloat(scorePercentage.toFixed(2)),
                                  isCompleted: true,
                                  filledQuestions: formData.filledQuestions || []
                              }
                            : phase
                    );
                } else {
                    // Phase doesn't exist, add it
                    const maxScore = formData.maxScore || 100;
                    const scorePercentage = maxScore > 0 ? ((phaseScore || 0) / maxScore) * 100 : 0;
                    
                    updatedFilledPhases = [
                        ...existingSection.filledPhases,
                        {
                            phasesId: formData.phasesId,
                            totalScore: phaseScore || 0,
                            scorePercentage: parseFloat(scorePercentage.toFixed(2)),
                            isCompleted: true,
                            filledQuestions: formData.filledQuestions || []
                        }
                    ];
                }

                updatedFilledSections = state.filledSections.map((section: FilledSection, idx: number) =>
                    idx === existingSectionIndex
                        ? {
                              ...section,
                              filledPhases: updatedFilledPhases
                          }
                        : section
                );
            } else {
                // Section doesn't exist, create new section with phase
                const maxScore = formData.maxScore || 100;
                const scorePercentage = maxScore > 0 ? ((phaseScore || 0) / maxScore) * 100 : 0;
                
                updatedFilledSections = [
                    ...state.filledSections,
                    {
                        sectionsId: formData.sectionsId,
                        filledPhases: [
                            {
                                phasesId: formData.phasesId,
                                totalScore: phaseScore || 0,
                                scorePercentage: parseFloat(scorePercentage.toFixed(2)),
                                isCompleted: true,
                                filledQuestions: formData.filledQuestions || []
                            }
                        ]
                    }
                ];
            }

            // Update lastCompletedPhasesIds array
            const updatedLastCompletedPhasesIds = lastCompletedPhasesId && !state.lastCompletedPhasesIds.includes(lastCompletedPhasesId)
                ? [...state.lastCompletedPhasesIds, lastCompletedPhasesId]
                : state.lastCompletedPhasesIds;

            return {
                ...state,
                lastCompletedPhasesIds: updatedLastCompletedPhasesIds,
                filledSections: updatedFilledSections
            }
        case SET_CURRENT_POSITION:
            return {
                ...state,
                currentSectionIndex: action.payload.sectionIndex,
                currentPhaseIndex: action.payload.phaseIndex
            }
        case SET_MESSAGE:
            return {
                ...state,
                message: action.payload
            }
        case SUBMIT_QUESTIONNAIRE_SUCCESS:
            return {
                ...state,
                submissionError: null
            }
        case SUBMIT_QUESTIONNAIRE_ERROR:
            return {
                ...state,
                submissionError: action.payload
            }
        case RESET:
            return initialState;
        default:
            return state;
    }
};

export default FilledQuestionnaireReducer;